import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import RightDrawer from "components/rightDrawer/RightDrawer";
import DateTimePicker from "components/ui/dateTimePicker";
import InputField from "components/ui/inputField";
import Select from "components/ui/select";
import { createPayment, updatePayment } from "features/payments/api/payments";
import { useFormik } from "formik";
import useApi from "hooks/useApi";
import useBase from "hooks/useBase";
import useQueries from "hooks/useQueries";
import moment from "moment";
import { useEffect, useState } from "react";
import ReactLoading from "react-loading";
import handleError from "utils/handleError";
import validationSchema from "../../schema";
import MultiSelect from "components/ui/multiSelect";

const AddEdit = ({
  persistedData,
  setPersistedData,
  openDrawer,
  setOpenDrawer,
  setAllPayments,
  allPayments,
  unpaidInvoices,
  invoices,
  paymentMethods,
  currencies,
  settings,
  banks,
  paymentCategories,
}) => {
  const { row_id } = useQueries();
  const isUpdating = row_id ? true : false;
  const [currPayment, setCurrPayment] = useState(row_id ? {} : persistedData);
  const [invoiceDetails, setInvoiceDetails] = useState(null);

  const { updateBase } = useBase();

  const handleClose = (reset) => {
    setPersistedData(row_id ? {} : reset ? {} : formik?.values);
    setOpenDrawer();
  };

  const { loading, fetchData } = useApi();
  const { loading: loadingInvoiceDetails, fetchData: fetchDataInvoiceDetails } =
    useApi();

  useEffect(() => {
    const getFullPayment = async () => {
      const endpoint = {
        method: "get",
        url: `/api/admin/payment/show/${row_id}`,
      };
      const result = await fetchData(endpoint, false);
      result.success && setCurrPayment(result?.data);
    };
    row_id && getFullPayment();
  }, []);

  const onSubmit = async (values) => {
    const { chooseVariant, ...data } = values;
    const result = await fetchData(
      isUpdating ? updatePayment(currPayment?.id, data) : createPayment(data),
    );
    if (result?.success) {
      if (isUpdating) {
        setAllPayments(
          allPayments.map((payment) =>
            payment.id === currPayment.id
              ? { ...currPayment, ...result.data }
              : payment,
          ),
        );
      } else {
        setAllPayments([result.data, ...allPayments]);
      }
      handleClose("reset");
    } else {
      handleError(result, formik);
    }
  };

  const currency_id = settings?.app?.currency_id;

  const initialValues = {
    chooseVariant: currPayment?.invoice_id
      ? "invoice"
      : currPayment?.payment_category_id
        ? "paymentCategory"
        : "invoice",
    invoice_id: currPayment?.invoice_id || "",
    payment_name: currPayment?.payment_name || "",
    payment_category_id: currPayment?.payment_category_id || "",
    payment_method_id:
      currPayment?.payment_method_id ||
      paymentMethods?.find((item) => item?.name === "Cash")?.id ||
      "",
    payment_date: currPayment?.payment_date || moment().format("YYYY-MM-DD"),
    due_date: currPayment?.due_date || null,
    currency_id: currPayment?.currency_id || currency_id || "",
    amount: currPayment?.amount || "",
    transaction_id: currPayment?.transaction_id || "",
    note: currPayment?.note || "",
    bank_id: currPayment?.bank_id || "",
    purpose: currPayment?.purpose || [],
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  useEffect(() => {
    const getProfile = async () => {
      const endpoint = {
        method: "get",
        url: `/api/admin/invoice/show/${formik.values.invoice_id}`,
      };
      const { success, data } = await fetchDataInvoiceDetails(endpoint, false);
      if (success) setInvoiceDetails(data);
    };
    getProfile();
  }, [formik.values.invoice_id]);

  useEffect(() => {
    updateBase(
      isUpdating ? ["invoices"] : ["unpaidInvoices", "paymentCategories"],
    );
  }, []);

  return (
    <RightDrawer
      {...{
        openDrawer,
        setOpenDrawer: handleClose,
        width: "850px",
        headingLeft: currPayment?.id ? "Edit Payment" : "New Payment",
        headingRight: "",
      }}
    >
      {loading ? (
        <Box
          sx={{
            height: "calc(100vh - 143px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ReactLoading
            type="cylon"
            color="#405189"
            height={"85px"}
            width={"100px"}
          />
        </Box>
      ) : (
        <Box p="20px">
          {!currPayment?.payment_category_id && !currPayment?.invoice_id && (
            <Stack direction="row" spacing={2} mb={2}>
              <RadioGroup
                row
                name="chooseVariant"
                value={formik.values.chooseVariant}
                onChange={async (e) => {
                  const isInvoice = e.target.value === "invoice";
                  formik.handleChange(e);
                  await formik.setFieldValue(
                    "invoice_id",
                    isInvoice ? currPayment?.invoice_id || "" : "",
                  );
                  await formik.setFieldValue(
                    "payment_category_id",
                    isInvoice ? "" : currPayment?.payment_category_id || "",
                  );

                  await formik.setFieldValue(
                    "payment_category_id",
                    isInvoice ? "" : currPayment?.payment_category_id || "",
                  );

                  await formik.setFieldValue(
                    "payment_name",
                    isInvoice ? "" : currPayment?.payment_name || "",
                  );
                  await formik.setFieldValue("purpose", []);
                }}
              >
                <FormControlLabel
                  value="invoice"
                  control={
                    <Radio
                      size="small"
                      sx={{ "&.Mui-checked": { bgcolor: "#e9f1fe" } }}
                    />
                  }
                  label="Invoice"
                  sx={{ "&.Mui-checked": { bgcolor: "#e9f1fe" } }}
                />
                <FormControlLabel
                  value="paymentCategory"
                  control={
                    <Radio
                      size="small"
                      sx={{ "&.Mui-checked": { bgcolor: "#e9f1fe" } }}
                    />
                  }
                  label="Payment Category"
                  sx={{
                    "&.Mui-checked": { bgcolor: "#e9f1fe" },
                  }}
                />
              </RadioGroup>
            </Stack>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "15px",
              pb: "15px",
            }}
          >
            {formik.values.chooseVariant === "invoice" && (
              <Select
                options={
                  // isUpdating ?
                  invoices
                  // : unpaidInvoices
                }
                label="Invoice *"
                name="invoice_id"
                selectProps={{ id: "id", label: "name" }}
                value={formik.values.invoice_id}
                onChange={async (value) => {
                  await formik.setFieldValue("invoice_id", value);
                  await formik.setFieldValue("purpose", []);
                }}
                error={
                  formik.touched.invoice_id && Boolean(formik.errors.invoice_id)
                }
                helperText={
                  formik.touched.invoice_id && formik.errors.invoice_id
                }
              />
            )}

            {formik.values.chooseVariant === "paymentCategory" && (
              <>
                <InputField
                  label="Payment Name *"
                  name="payment_name"
                  formik={formik}
                  sx={{ gridColumn: "span 2" }}
                />
                <Select
                  options={paymentCategories}
                  label="Payment Category *"
                  name="payment_category_id"
                  selectProps={{ id: "id", label: "name" }}
                  formik={formik}
                />
              </>
            )}

            <InputField
              label="Transaction ID"
              name="transaction_id"
              formik={formik}
            />
            <DateTimePicker
              name="payment_date"
              label="Payment Date *"
              formik={formik}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "15px",
              }}
            >
              <Select
                options={paymentMethods}
                label="Pay Method *"
                name="payment_method_id"
                selectProps={{ id: "id", label: "name" }}
                formik={formik}
              />
              <Select
                options={banks}
                label="Banks"
                value={formik?.values?.bank_id}
                onChange={(value) => formik.setFieldValue("bank_id", value)}
                selectProps={{ id: "id", label: "label" }}
              />
            </Box>
            <Box
              sx={{
                gridColumn: "span 2",
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "15px",
              }}
            >
              <Select
                options={currencies}
                label="Currency *"
                name="currency_id"
                selectProps={{ id: "id", label: "name" }}
                formik={formik}
              />
              <InputField label="Amount *" name="amount" formik={formik} />
              <DateTimePicker
                name="due_date"
                label="Due Date"
                formik={formik}
              />
            </Box>

            {formik.values.chooseVariant === "invoice" && (
              <MultiSelect
                options={invoiceDetails?.invoice_details || []}
                label="Purpose"
                value={
                  // Derive selected ids from formatted purpose strings
                  (invoiceDetails?.invoice_details || [])
                    .filter((detail) =>
                      (formik?.values?.purpose || []).includes(
                        detail.refund_type
                          ? `${detail.item} (${detail.refund_type})`
                          : detail.item,
                      ),
                    )
                    .map((detail) => detail.id)
                }
                onChange={(selectedIds) => {
                  // Format selected items as "item (refund_type)" or just "item"
                  const formattedPurpose = (
                    invoiceDetails?.invoice_details || []
                  )
                    .filter((detail) => selectedIds.includes(detail.id))
                    .map((detail) =>
                      detail.refund_type
                        ? `${detail.item} (${detail.refund_type})`
                        : detail.item,
                    );
                  formik.setFieldValue("purpose", formattedPurpose);
                }}
                selectProps={{ id: "id", label: "item" }}
                disabled={
                  formik.values.invoice_id
                    ? false
                    : true || loadingInvoiceDetails
                }
              />
            )}

            <InputField
              sx={{ gridColumn: "span 2" }}
              name="note"
              label="Note"
              multiline
              rows={2}
              formik={formik}
            />
            <Button
              sx={{ gridColumn: "span 2" }}
              variant="solid"
              onClick={formik.submitForm}
              fullWidth
              disabled={!(formik.dirty && formik.isValid)}
            >
              {currPayment?.id ? "Update Payment" : "Save Payment"}
            </Button>
          </Box>
        </Box>
      )}
    </RightDrawer>
  );
};

export default AddEdit;
